/* AT2 EXPERIMENT - Second Message Verification - Custom Hook */

/**
 * V2 COMPLET - Flux AT2 avec analyse + génération + interaction utilisateur
 *
 * Flow V2:
 * 🟠 1. Point de départ — Envoi AT2
 * 🔶 2. Vérification du stockage — Analyse dans le stockage ?
 * 🟤 3. Phase d'analyse — Popup + Requête + Attente + Stockage
 * 🟧 6. Phase de génération — Popup + Requête + Génération + Stockage
 * 🔶 7. Interaction utilisateur — Affichage modal comparaison
 * 🔷 8. Actions utilisateur — Accepter / Refuser / Modifier
 *
 * Ce hook gère TOUTE la logique métier AT2 (analyse, génération, handlers, modals)
 */

import { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { DossierId } from '../../../../../../../resources/dossier/types';
import { generateSendingMessageId } from '../../../../../../../resources/discussion';
import type { AT2Analysis } from '../mock/at2-mock-api';

import { getCache, saveCache, clearCache } from '../utils/at2-cache-utils';
import { callAnalyzeAPI, callGenerateAPI, callShadowAnalyzeAPI } from '../utils/at2-api-utils';
import { useGetDossierData } from '../../../../../../../resources/dossier/queries';
import { useAgentMessageCount } from '../../../../../../../resources/discussion/queries';
import {
    useExperimentVariation,
    useGetTrackedTripProperties,
    useTrackEventAmplitude,
} from '../../../../../../../shared/analytics';
import { getFeatureFlag } from '../../../../../../../shared/feature-flags/getFeatureFlag';
import { LatencyLogger } from '../utils/at2-logger-utils';
import { EditorContent } from '../../../../../../../shared/components/QuillEditor/QuillEditor';

export type AT2Mode = 'send' | 'schedule';

export type AT2State = {
    // 🔶 Phase - Stockage
    analyzed: boolean;
    analyzing: boolean;

    // 🟤 Phase - Analyse
    originalMessage: string;
    needsOptimization: boolean;
    missingElements: AT2Analysis['missingElements'] | null;

    // 🟧 Phase - Génération
    generating: boolean;
    generated: boolean;
    improvedMessage: string | null;

    // 🔷 Phase - Mode (send ou schedule)
    mode: AT2Mode;

    // 🔷 Phase - Erreur (simple, pas de retry en V2)
    error: string | null;
};

export type AT2ModalState = {
    showAnalysisModal: boolean;
    showComparisonModal: boolean;
    showSendingModal: boolean;
};

type AT2AnalysisResult = {
    success: boolean;
    needsOptimization: boolean;
    shouldStopFlow: boolean;
};

/** Callbacks nécessaires pour l'envoi/programmation des messages */
type AT2SendCallbacks = {
    onSendMessage: (dossierId: DossierId, sendingMessageId: string, message: string) => void;
    onOpenScheduleDialog: (message: string) => void;
    setSendingMessageId: (id: string) => void;
    setContent: (content: EditorContent | null) => void;
};

/**
 * Hook complet V2 pour la vérification AT2 avec génération.
 * Centralise TOUTE la logique métier AT2 dans le hook.
 *
 * Architecture:
 * - API calls pures (callAnalysisAPI, callGenerationAPI) → pas de state management
 * - handleAT2Flow → gère TOUT le state (analyzing, generating, modals, etc.)
 * - Handlers d'envoi → actions utilisateur après choix dans le modal
 *
 * Usage:
 * ```typescript
 * const {
 *   handleAT2Flow,           // Flow principal (appeler dans handleSubmit)
 *   handleSendImprovedMessage, // Envoyer version optimisée
 *   handleSendOriginalMessage, // Envoyer version originale
 *   at2State,                // État AT2 (analyzing, generating, etc.)
 *   modalState               // État des modals
 * } = useAT2MessageCheck(dossierId, sendingMessageId, callbacks);
 * ```
 */
export function useAT2MessageCheck(
    dossierId: DossierId,
    sendingMessageId: string,
    callbacks: AT2SendCallbacks,
) {
    const { dossier } = useGetDossierData(dossierId) || {};
    const { project } = dossier || {};
    const agentMessageCount = useAgentMessageCount(dossierId);
    const { locale } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const popinModeVariant = useExperimentVariation('abMaturityLatencyPopinMode');
    const isShadowModeEnable = popinModeVariant?.value !== 'on';

    const [at2State, setAT2State] = useState<AT2State>({
        // Phase analyse
        originalMessage: '',
        analyzed: false,
        analyzing: false,
        needsOptimization: false,
        missingElements: null,
        // Phase génération
        generating: false,
        generated: false,
        improvedMessage: '',
        // Mode
        mode: 'send',
        // Erreur
        error: null,
    });

    const [modalState, setModalState] = useState<AT2ModalState>({
        showAnalysisModal: false,
        showComparisonModal: false,
        showSendingModal: false,
    });

    /**
     * Réinitialise l'état AT2 (après envoi du message)
     */
    const resetAT2 = useCallback(() => {
        setAT2State({
            // Phase analyse
            originalMessage: '',
            analyzed: false,
            analyzing: false,
            needsOptimization: false,
            missingElements: null,
            // Phase génération
            generating: false,
            generated: false,
            improvedMessage: null,
            // Mode
            mode: 'send',
            // Erreur
            error: null,
        });
        setModalState({
            showAnalysisModal: false,
            showComparisonModal: false,
            showSendingModal: false,
        });
    }, []);

    /**
     * 🟠 Step 1-7: Flow complet AT2 lors du submit
     * Gère TOUT le state management en utilisant analyzeMessage/generateMessage
     * Retourne si le flow normal doit continuer ou s'arrêter
     */
    const handleAT2Flow = useCallback(
        async (message: string, mode: AT2Mode = 'send'): Promise<AT2AnalysisResult> => {
            const latencyLogger = new LatencyLogger(dossierId, locale, sendingMessageId);
            const isAbMaturityLatencyExperimentEnabled = getFeatureFlag(
                'abMaturityLatencyExperiment',
            );
            const isDossierInLatencyTest =
                isAbMaturityLatencyExperimentEnabled && project?.maturityExperiments?.isLatencyTest;

            //Test si et seulement si le dossier est dans le test latency et que l'agent n'a envoyé aucun message (le 1er étant AT1 automatic)
            if (
                agentMessageCount !== 1 ||
                !isDossierInLatencyTest ||
                !isAbMaturityLatencyExperimentEnabled
            ) {
                console.log('DEBUG - AT2: Flow stopped because conditions not met', {
                    agentMessageCount,
                    isDossierInLatencyTest,
                    isAbMaturityLatencyExperimentEnabled,
                });
                return { success: true, needsOptimization: false, shouldStopFlow: false };
            }

            if (isShadowModeEnable) {
                void callShadowAnalyzeAPI(dossierId, message, locale);
                latencyLogger.info('A', 'Shadow Analyze and Generate API called', {
                    message,
                });
                return { success: true, needsOptimization: false, shouldStopFlow: false };
            }

            latencyLogger.info('O', 'AT2 flow started', {
                message,
                mode,
            });

            // Si l'analyse a déjà été faite et qu'optimisation était nécessaire, réafficher la modal
            if (at2State.analyzed && at2State.needsOptimization) {
                // Mettre à jour le mode au cas où il aurait changé (send vs schedule)
                setAT2State((prev) => ({ ...prev, mode }));
                // Réouvrir la modal de comparaison
                setModalState((prev) => ({ ...prev, showComparisonModal: true }));
                return { success: true, needsOptimization: true, shouldStopFlow: true };
            }

            // Si l'analyse n'a pas encore été faite
            if (!at2State.analyzed) {
                // Stocker le message original et le mode pour le modal
                setAT2State((prev) => ({ ...prev, originalMessage: message, mode }));

                trackEvent((ampli, defaultProperties) => {
                    ampli.at2MessageCheckStarted({
                        ...defaultProperties,
                        ...trackedTripProperties,
                        message_id: Number(sendingMessageId),
                    });
                });

                // 🔶 Vérifier d'abord le cache
                const cached = await getCache(dossierId, message);
                let analysis: AT2Analysis;

                if (cached?.analysis) {
                    // ✅ Cache trouvé - utiliser l'analyse en cache
                    latencyLogger.info('B', 'Use cached for AT2 analysis', {
                        message,
                    });
                    ({ analysis } = cached);

                    // Mettre à jour l'état avec les données en cache
                    setAT2State((prev) => ({
                        ...prev,
                        analyzed: true,
                        analyzing: false,
                        needsOptimization: analysis.needsOptimization,
                        missingElements: analysis.missingElements,
                    }));
                } else {
                    // 🟤 Step 3: Début de l'analyse (pas de cache)
                    setAT2State((prev) => ({ ...prev, analyzing: true, error: null }));
                    setModalState((prev) => ({ ...prev, showAnalysisModal: true }));

                    latencyLogger.info('C', 'Call Analyze API', {
                        message,
                    });

                    // 🟤 Appel de l'API d'analyse (avec error handling et locale)
                    const analysisResult = await callAnalyzeAPI(dossierId, message, locale);

                    if (!analysisResult.success) {
                        latencyLogger.error(
                            'D',
                            'Analyze API failed, continue with original message',
                            {
                                message,
                            },
                            analysisResult.error,
                        );

                        // ❌ Erreur d'analyse - fermer la modal et continuer le flow normal
                        setAT2State((prev) => ({
                            ...prev,
                            analyzing: false,
                            error: analysisResult.error,
                        }));
                        setModalState((prev) => ({ ...prev, showAnalysisModal: false }));
                        // En cas d'erreur, continuer le flow normal (envoi sans AT2)
                        return { success: true, needsOptimization: false, shouldStopFlow: false };
                    }

                    // ✅ Analyse réussie - TypeScript sait maintenant que analysisResult.success === true
                    const { analysis: apiAnalysis } = analysisResult;
                    analysis = apiAnalysis;

                    latencyLogger.info('E', 'Analyze API success, AT2 analysis displayed', {
                        message,
                        needsOptimization: analysis.needsOptimization,
                        missingElements: analysis.missingElements,
                    });

                    // 🟨 Step 5: Stockage du résultat d'analyse dans l'état
                    setAT2State((prev) => ({
                        ...prev,
                        analyzed: true,
                        analyzing: false,
                        needsOptimization: analysis.needsOptimization,
                        missingElements: analysis.missingElements,
                    }));

                    // 💾 Sauvegarder l'analyse dans le cache
                    await saveCache(dossierId, message, { analysis });
                }

                trackEvent((ampli, defaultProperties) => {
                    ampli.at2MessageCheckResult({
                        ...defaultProperties,
                        ...trackedTripProperties,
                        message_id: Number(sendingMessageId),
                        at2_need_correction: analysis.needsOptimization,
                    });
                });

                // Si optimisation nécessaire → ouvrir modal + générer
                if (analysis.needsOptimization) {
                    // Ouvrir le modal de comparaison
                    setModalState((prev) => ({
                        ...prev,
                        showAnalysisModal: false,
                        showComparisonModal: true,
                    }));

                    // 🔶 Vérifier si le message amélioré est déjà en cache
                    if (cached?.improvedMessage) {
                        // ✅ Cache trouvé - utiliser le message amélioré en cache
                        latencyLogger.info('F', 'Use cached for AT2 generation', {
                            message,
                            improvedMessage: cached.improvedMessage,
                        });

                        setAT2State((prev) => ({
                            ...prev,
                            generating: false,
                            generated: true,
                            improvedMessage: cached.improvedMessage,
                        }));

                        trackEvent((ampli, defaultProperties) => {
                            ampli.at2MessageEditSuggestionDisplayed({
                                ...defaultProperties,
                                ...trackedTripProperties,
                                message_id: Number(sendingMessageId),
                            });
                        });
                    } else {
                        // 🟧 Step 6: Lancer la génération en background (pas de cache)
                        setAT2State((prev) => ({ ...prev, generating: true }));

                        latencyLogger.info('G', 'Call AT2 generation API', {
                            message,
                            analysis,
                        });

                        // 🟧 Appel de l'API de génération (avec error handling)
                        const generationResult = await callGenerateAPI(
                            dossierId,
                            message,
                            analysis,
                            locale,
                        );

                        if (!generationResult.success) {
                            latencyLogger.error(
                                'H',
                                'AT2 generation API failed, continue with original message',
                                {
                                    message,
                                    analysis,
                                },
                                generationResult.error,
                            );

                            // ❌ Erreur de génération - fermer la modal et continuer avec le message original
                            setAT2State((prev) => ({
                                ...prev,
                                generating: false,
                                error: generationResult.error,
                            }));
                            setModalState((prev) => ({ ...prev, showComparisonModal: false }));

                            // Envoyer le message original en mode send, ou ouvrir le dialog de schedule
                            if (mode === 'schedule') {
                                callbacks.onOpenScheduleDialog(message);
                            } else {
                                // Mode envoi: afficher modal d'envoi et envoyer le message original
                                setModalState((prev) => ({ ...prev, showSendingModal: true }));
                                callbacks.onSendMessage(dossierId, sendingMessageId, message);
                                callbacks.setContent(null);
                                callbacks.setSendingMessageId(generateSendingMessageId());
                            }

                            // Arrêter le flow car on a déjà envoyé/programmé
                            return { success: true, needsOptimization: true, shouldStopFlow: true };
                        } else {
                            // ✅ Génération réussie - TypeScript sait que generationResult.success === true
                            const { improvedMessage } = generationResult;

                            latencyLogger.info(
                                'I',
                                'Generate API success, AT2 generated displayed',
                                {
                                    message,
                                    analysis,
                                    improvedMessage,
                                },
                            );

                            setAT2State((prev) => ({
                                ...prev,
                                generating: false,
                                generated: true,
                                improvedMessage,
                            }));

                            // 💾 Sauvegarder le message amélioré dans le cache
                            await saveCache(dossierId, message, { improvedMessage });

                            trackEvent((ampli, defaultProperties) => {
                                ampli.at2MessageEditSuggestionDisplayed({
                                    ...defaultProperties,
                                    ...trackedTripProperties,
                                    message_id: Number(sendingMessageId),
                                });
                            });
                        }
                    }

                    // 🔶 Step 7: Arrêter le flow - attendre action utilisateur
                    return { success: true, needsOptimization: true, shouldStopFlow: true };
                }

                // ✅ Pas d'optimisation nécessaire
                if (mode === 'send') {
                    // Mode envoi: afficher modal d'envoi
                    setModalState((prev) => ({ ...prev, showSendingModal: true }));
                }
                // Mode schedule: pas de modal d'envoi, le flow continue vers ScheduleMessageDialog
            }

            // Continuer le flow normal (AT2 validation OK ou pas de vérification nécessaire)
            return { success: true, needsOptimization: false, shouldStopFlow: false };
        },
        [
            dossierId,
            locale,
            sendingMessageId,
            project?.maturityExperiments?.isLatencyTest,
            agentMessageCount,
            isShadowModeEnable,
            at2State.analyzed,
            at2State.needsOptimization,
            trackEvent,
            trackedTripProperties,
            callbacks,
        ],
    );

    /**
     * 🔶 Fermer le modal de comparaison (sans envoyer)
     */
    const closeComparisonModal = useCallback(() => {
        setModalState((prev) => ({ ...prev, showComparisonModal: false }));
    }, []);

    /**
     * 🔷 Envoyer ou programmer la version optimisée du message (éventuellement modifiée par l'utilisateur)
     */
    const handleSendImprovedMessage = useCallback(
        async (editedMessage: string) => {
            if (!editedMessage.trim()) return;

            trackEvent((ampli, defaultProperties) => {
                ampli.at2MessageActionClicked({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    message_id: Number(sendingMessageId),
                    at2_action_type: 'optimized_version',
                });
            });

            // 🧹 Nettoyer le cache du message original
            await clearCache(dossierId, at2State.originalMessage);

            // Fermer le modal de comparaison
            setModalState((prev) => ({ ...prev, showComparisonModal: false }));

            if (at2State.mode === 'schedule') {
                // Mode programmation: ouvrir le dialog de programmation avec le message amélioré
                callbacks.onOpenScheduleDialog(editedMessage);
            } else {
                // Mode envoi: envoyer directement et afficher le modal d'envoi
                setModalState((prev) => ({ ...prev, showSendingModal: true }));
                callbacks.onSendMessage(dossierId, sendingMessageId, editedMessage);
                callbacks.setContent(null);
                callbacks.setSendingMessageId(generateSendingMessageId());
            }
        },
        [
            callbacks,
            dossierId,
            sendingMessageId,
            at2State.mode,
            at2State.originalMessage,
            trackEvent,
            trackedTripProperties,
        ],
    );

    /**
     * Envoyer ou programmer la version originale (ignorer les suggestions AT2)
     */
    const handleSendOriginalMessage = useCallback(async () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.at2MessageActionClicked({
                ...defaultProperties,
                ...trackedTripProperties,
                message_id: Number(sendingMessageId),
                at2_action_type: 'my_version',
            });
        });

        // 🧹 Nettoyer le cache du message original
        await clearCache(dossierId, at2State.originalMessage);

        // Fermer le modal de comparaison
        setModalState((prev) => ({ ...prev, showComparisonModal: false }));

        if (at2State.mode === 'schedule') {
            // Mode programmation: ouvrir le dialog de programmation avec le message original
            callbacks.onOpenScheduleDialog(at2State.originalMessage);
        } else {
            // Mode envoi: envoyer directement et afficher le modal d'envoi
            setModalState((prev) => ({ ...prev, showSendingModal: true }));
            callbacks.onSendMessage(dossierId, sendingMessageId, at2State.originalMessage);
            callbacks.setContent(null);
            callbacks.setSendingMessageId(generateSendingMessageId());
        }
    }, [
        callbacks,
        dossierId,
        sendingMessageId,
        at2State.originalMessage,
        at2State.mode,
        trackEvent,
        trackedTripProperties,
    ]);

    /**
     * Fermer automatiquement le modal d'envoi après 2 secondes
     */
    useEffect(() => {
        if (modalState.showSendingModal) {
            const timer = setTimeout(() => {
                // Nettoyer AT2 state après l'envoi
                resetAT2();
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [modalState.showSendingModal, resetAT2]);

    return {
        // 🟠 Flow principal AT2 (analyse + génération + state management)
        handleAT2Flow,
        // 🔷 Handlers d'envoi (après choix utilisateur)
        handleSendImprovedMessage,
        handleSendOriginalMessage,
        // 🔶 Actions modals
        closeComparisonModal,
        // 📊 États (lecture seule pour les composants)
        at2State,
        modalState,
        // 🔧 Utilitaires
        resetAT2,
    };
}
